// react
import React, { useState, useEffect, useCallback } from "react";

// prop-types
import PropTypes from "prop-types";

// context
import { UserAuth } from "context/AuthContext";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Box, Grid, Button, CircularProgress, FormControl, InputLabel, OutlinedInput, Select, MenuItem, Checkbox, ListItemText, Card, useTheme } from "@mui/material";
import { FileDownload as FileDownloadIcon, Search as SearchIcon, Autorenew as AutorenewIcon } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

// date-fns
import { enUS, it } from "date-fns/locale";

// artifacts/mps
import MpsTable from "./MpsTable";
import MpsChart from "./MpsChart";
import ExportCsvDialog from "./ExportCsvDialog";

// components
import DataDialog from "components/dialogs/DataDialog";
import DatapointIntegrityInspector from "components/dialogs/DatapointIntegrityInspector";
import { LoadingDialog } from "components/loading/LoadingComponent";

// firebase
import { db } from "config/firebase";
import { doc, collection, onSnapshot, query, where } from "firebase/firestore";

// features/record
import fetchRecordsByType from "features/record/fetchRecordsByType";

// lib/verification
import { verifySignature } from "lib/verification";

// A ---------------------------------------------------------------------- M

const MpsViewer = ({ tag, isTagGroupMember }) => {
  const { user } = UserAuth();
  const { t, i18n } = useTranslation();
  const isItalian = i18n.language === "it";
  const dateLocale = isItalian ? it : enUS;
  const dateFormat = isItalian ? "dd/MM/yyyy HH:mm" : "MM/dd/yyyy hh:mm aa";
  const theme = useTheme();

  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState();
  const [verification, setVerification] = useState();
  const [selectedSensors, setSelectedSensors] = useState([]);
  const [commonMagnitudes, setCommonMagnitudes] = useState([]);
  const [selectedMagnitude, setSelectedMagnitude] = useState("");

  const [chartData, setChartData] = useState([]);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [openCsvModal, setOpenCsvModal] = useState(false);
  const [openVerify, setOpenVerify] = useState(false);
  const [openView, setOpenView] = useState(false);

  const [startDate, setStartDate] = useState(new Date(Date.now() - 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(new Date());

  const [isRealTime, setIsRealTime] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [verificationLoading, setVerificationLoading] = useState(false);

  // Style
  const toolbarCardStyle = {
    p: 2,
    borderRadius: 3,
    background: `linear-gradient(135deg,
      ${theme.palette.background.paper}EE,
      ${theme.palette.background.default}F8)`,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: "0 4px 16px rgba(0,0,0,0.08)",
    backdropFilter: "blur(8px)",
  };

  const softButton = {
    py: 1,
    px: 2,
    borderRadius: 3,
    fontWeight: 600,
    textTransform: "none",
    background: `${theme.palette.primary.main}15`,
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}33`,
    "&:hover": {
      background: `${theme.palette.primary.main}25`,
      borderColor: `${theme.palette.primary.main}55`,
    },
  };

  const inputSoft = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      backgroundColor: theme.palette.background.paper,
    },
  };

  // Handle popstate (drawer)
  useEffect(() => {
    const handlePopState = () => {
      if (openVerify) setOpenVerify(false);
      else if (openView) setOpenView(false);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [openVerify, openView]);

  // Fetch & group records
  const fetchData = useCallback(async (start, end, isRealtimeUpdate = false) => {
    try {
      if (!isRealtimeUpdate) setIsLoading(true);
      const newRecords = await fetchRecordsByType(tag.id, "mps", isTagGroupMember, start, end);
      processRecords(newRecords);
    } catch (error) {
      console.error("Error fetching:", error);
    } finally {
      if (!isRealtimeUpdate) setIsLoading(false);
    }
  }, []);

  const processRecords = (records) => {
    let groupedRecords = Object.values(
      records.reduce((acc, curr) => {
        const sensorId = curr.creator_uuid;
        const sensorName = curr.creator_email || sensorId;
        const magnitude = curr.data.magnitude || curr.data.uom;

        if (!sensorId) return acc;

        if (!acc[sensorId]) {
          acc[sensorId] = { sensorId, sensorName, magnitudes: [] };
        }

        const group = acc[sensorId].magnitudes.find((g) => g.magnitude === magnitude);
        if (!group) {
          acc[sensorId].magnitudes.push({
            magnitude,
            records: [curr],
          });
        } else {
          group.records.push(curr);
        }

        return acc;
      }, {})
    );

    groupedRecords.forEach((sensor) => {
      sensor.magnitudes.forEach((m) => m.records.sort((a, b) => a.data.timestamp_end - b.data.timestamp_end));
    });

    setRecords(groupedRecords);
  };

  // Realtime listener
  useEffect(() => {
    if (isRealTime) {
      const tagsRef = doc(db, "tagsdata", tag.id);
      const signaturesRef = collection(tagsRef, "signatures");
      const ts = (Date.now() / 1000).toString();

      const q = query(signaturesRef, where("type", "==", "mps"), where("timestamp", ">", ts));

      const unsub = onSnapshot(q, (snapshot) => {
        snapshot.docChanges().forEach((chg) => {
          if (chg.type === "added") {
            fetchData(new Date(Date.now() - 24 * 60 * 60 * 1000), new Date(), true);
          }
        });
      });

      fetchData(new Date(Date.now() - 24 * 60 * 60 * 1000), new Date());
      return () => unsub();
    }
  }, [isRealTime]);

  // Reset states
  const resetStates = () => {
    setRecords([]);
    setSelectedSensors([]);
    setCommonMagnitudes([]);
    setSelectedMagnitude("");
    setChartData([]);
  };

  // Search & realtime actions
  const handleSearch = () => {
    setIsRealTime(false);
    resetStates();
    fetchData(startDate, endDate);
  };

  const handleRealTime = () => {
    const newStart = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const newEnd = new Date();

    setIsRealTime(true);
    resetStates();

    setStartDate(newStart);
    setEndDate(newEnd);

    fetchData(newStart, newEnd);
  };

  // Sensor selection
  useEffect(() => {
    if (records.length > 0) {
      const all = records.map((r) => r.sensorId);
      setSelectedSensors(all);
    }
  }, [records]);

  // Magnitudes
  useEffect(() => {
    const all = new Set();
    selectedSensors.forEach((id) => {
      const sensor = records.find((r) => r.sensorId === id);
      sensor?.magnitudes.forEach((m) => all.add(m.magnitude));
    });
    setCommonMagnitudes([...all]);
  }, [selectedSensors]);

  // Build chart data for recharts
  useEffect(() => {
    if (!selectedMagnitude || selectedSensors.length === 0) return;

    const map = {};

    selectedSensors.forEach((sensorId) => {
      const sensor = records.find((r) => r.sensorId === sensorId);
      const sensorName = sensor?.sensorName || sensorId;

      const magnitudeGroup = sensor?.magnitudes.find((m) => m.magnitude === selectedMagnitude);

      magnitudeGroup?.records.forEach((rec) => {
        const ts = rec.data.timestamp * 1000;

        if (!map[ts]) map[ts] = { time: ts };
        map[ts][sensorName] = rec.data.value;
      });
    });

    const final = Object.values(map).sort((a, b) => a.time - b.time);

    setChartData(final);
  }, [records, selectedMagnitude]);

  // Sensor change
  const handleChangeSensors = (event) => {
    const value = event.target.value;

    if (value.includes("all")) {
      if (selectedSensors.length === records.length) {
        setSelectedSensors([]);
        setChartData([]);
      } else {
        setSelectedSensors(records.map((r) => r.sensorId));
      }
      return;
    }

    setSelectedSensors(value);
    if (value.length === 0) setChartData([]);
  };

  // Magnitude change
  const handleChangeSelectedMagnitude = (event) => setSelectedMagnitude(event.target.value);

  // Record view / verify
  const handleOpenVerify = () => {
    setOpenVerify(true);
    window.history.pushState(null, "");
  };

  const handleView = (record) => {
    setOpenView(true);
    setRecord(record);
    window.history.pushState(null, "");
  };

  const checkVerification = async (record) => {
    try {
      setVerificationLoading(true);
      const ver = user ? await verifySignature(user.uid, record, record.type) : await verifySignature(false, record, record.type);
      setVerification(ver);
      handleOpenVerify();
    } catch (e) {
      console.error("Error verification:", e);
    } finally {
      setVerificationLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Grid container item justifyContent="center">
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <>
      <Grid container spacing={3}>
        {/* TOOLBAR */}
        <Grid item xs={12}>
          <Card sx={toolbarCardStyle}>
            <Grid container spacing={2}>
              {/* DATE FILTERS */}
              <Grid item xs={12} sm={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                  <DateTimePicker
                    label={t("start_date")}
                    value={startDate}
                    onChange={setStartDate}
                    format={dateFormat}
                    slotProps={{
                      textField: { sx: { width: "100%", ...inputSoft } },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              <Grid item xs={12} sm={3}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                  <DateTimePicker
                    label={t("end_date")}
                    value={endDate}
                    onChange={setEndDate}
                    format={dateFormat}
                    slotProps={{
                      textField: { sx: { width: "100%", ...inputSoft } },
                    }}
                  />
                </LocalizationProvider>
              </Grid>

              {/* BUTTONS */}
              <Grid item xs={4} sm={2}>
                <Button fullWidth startIcon={<SearchIcon />} onClick={handleSearch} sx={softButton}>
                  {t("search")}
                </Button>
              </Grid>

              <Grid item xs={4} sm={2}>
                <Button
                  fullWidth
                  startIcon={
                    <AutorenewIcon
                      sx={{
                        ...(isRealTime && {
                          animation: "spin 2s linear infinite",
                        }),
                        "@keyframes spin": {
                          "0%": { transform: "rotate(0deg)" },
                          "100%": { transform: "rotate(360deg)" },
                        },
                      }}
                    />
                  }
                  onClick={handleRealTime}
                  sx={{
                    ...softButton,
                    ...(isRealTime && {
                      color: theme.palette.success.main,
                      borderColor: theme.palette.success.main,
                      background: theme.palette.success.light + "15",
                      "&:hover": {
                        background: theme.palette.success.light + "25",
                      },
                    }),
                  }}
                >
                  {isRealTime ? (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {t("realtime_on")}
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          background: theme.palette.error.main,
                          animation: "blink 1s infinite",
                          "@keyframes blink": {
                            "0%": { opacity: 1 },
                            "50%": { opacity: 0.2 },
                            "100%": { opacity: 1 },
                          },
                        }}
                      />
                    </Box>
                  ) : (
                    t("realtime")
                  )}
                </Button>
              </Grid>

              <Grid item xs={4} sm={2}>
                <Button fullWidth startIcon={<FileDownloadIcon />} onClick={() => setOpenCsvModal(true)} sx={softButton}>
                  {t("export")}
                </Button>
              </Grid>
            </Grid>

            {/* SENSOR & MAGNITUDE */}
            <Grid container spacing={2} mt={1}>
              {/* SENSORS */}
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth sx={inputSoft}>
                  <InputLabel>{t("source_name")}</InputLabel>
                  <Select
                    multiple
                    value={selectedSensors}
                    onChange={handleChangeSensors}
                    input={<OutlinedInput label={t("source_name")} />}
                    renderValue={(selected) =>
                      selected
                        .map((id) => {
                          const s = records.find((r) => r.sensorId === id);
                          return s ? s.sensorName : id;
                        })
                        .join(", ")
                    }
                  >
                    {records
                      .sort((a, b) => a.sensorName.localeCompare(b.sensorName))
                      .map((s) => (
                        <MenuItem key={s.sensorId} value={s.sensorId}>
                          <Checkbox checked={selectedSensors.includes(s.sensorId)} />
                          <ListItemText primary={s.sensorName} />
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* MAGNITUDE */}
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth sx={inputSoft}>
                  <InputLabel>{t("magnitude")}</InputLabel>
                  <Select value={selectedMagnitude} onChange={handleChangeSelectedMagnitude} input={<OutlinedInput label={t("magnitude")} />}>
                    {commonMagnitudes.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* CHART */}
        <Grid item xs={12}>
          <MpsChart chartData={chartData} />
        </Grid>

        {/* TABLE */}
        {selectedSensors.length > 0 && (
          <Grid item xs={12}>
            <MpsTable
              records={records}
              selectedSensors={selectedSensors}
              checkVerification={checkVerification}
              handleView={handleView}
              page={page}
              setPage={setPage}
              rowsPerPage={rowsPerPage}
              setRowsPerPage={setRowsPerPage}
            />
          </Grid>
        )}
      </Grid>

      {/* EXPORT */}
      <ExportCsvDialog tag={tag.id} open={openCsvModal} handleClose={() => setOpenCsvModal(false)} isTagGroupMember={isTagGroupMember} />

      {/* DIALOGS */}
      {record && <DataDialog data={{ ...record, databoxName: tag.name || "N/A" }} open={openView} onClose={() => setOpenView(false)} />}

      {verificationLoading && <LoadingDialog open={verificationLoading} />}

      {verification && <DatapointIntegrityInspector verification={verification} open={openVerify} setOpen={setOpenVerify} />}
    </>
  );
};

MpsViewer.propTypes = {
  tag: PropTypes.object.isRequired,
  isTagGroupMember: PropTypes.bool.isRequired,
};

export default MpsViewer;
